import { ChangeEvent, memo, useCallback, useEffect, useState } from 'react';

import { Theme } from '@mui/material';

import {
  Box,
  styled,
  TextField as BaseTextField,
  Typography,
  useLoading,
} from '@sbv-clients/ui-shared';

const style = (theme: Theme) => ({
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '14px',
  letterSpacing: '-0.12px',
  svg: {
    fill: theme.palette.primary.main,
  },
});

const TextField = styled(BaseTextField)`
  height: 100%;

  .MuiOutlinedInput-root {
    height: 100%;

    & fieldset {
      border-width: 0;
      border-radius: 0;
      font-size: smaller;
    }

    input {
      font-size: smaller;
      padding-left: 0;
      padding-right: 0;
      text-align: center;
    }
  }
`;

type onSubmitType = (value: string) => void;

type EditableViewProps = {
  type: 'number' | 'text';
  defaultValue: string;
  onSubmit: onSubmitType;
  addNew?: boolean | undefined;
  sx?: any;
  disabled?: boolean;
};
const Editable = ({
  type,
  defaultValue,
  onSubmit,
  addNew,
  sx,
  disabled,
}: EditableViewProps) => {
  const [focused, setFocused] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const [value, setValue] = useState<string>(defaultValue);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value || '0');
  };
  const onClick = useCallback(() => {
    setFocused(true);
  }, []);

  const handleSubmit: onSubmitType = useCallback(
    async (val) => {
      try {
        showLoading();
        onSubmit(val);
      } catch (e) {
        console.error('Error');
      } finally {
        setFocused(false);
        hideLoading();
      }
    },
    [hideLoading, onSubmit, showLoading]
  );

  const handleBlur = useCallback(async () => {
    const currValue = type === 'number' ? Number(value) : value;
    const defaultVal = type === 'number' ? Number(defaultValue) : defaultValue;

    setFocused(false);
    if (currValue !== defaultVal) {
      handleSubmit(value);
    }
  }, [value, type, defaultValue, handleSubmit]);

  return (
    <Box
      height="100%"
      minHeight={32}
      display="flex"
      alignItems="center"
      onClick={onClick}
    >
      {focused || addNew ? (
        <Box position="relative" width="100%">
          <TextField
            value={value}
            size="small"
            type={type}
            fullWidth
            onBlur={handleBlur}
            onChange={onChange}
            sx={{
              boxShadow: (theme: Theme) => theme.colors?.shadows.inputs,
              borderRadius: (theme: Theme) =>
                theme.borderRadius?.borderRadiusSmall,
              ...sx,
            }}
            disabled={disabled}
          />
        </Box>
      ) : (
        <Box>
          <Typography sx={(theme) => style(theme)}>{value}</Typography>
        </Box>
      )}
    </Box>
  );
};

const EditableNumberOrTextCell = memo(Editable);
export { EditableNumberOrTextCell };
